import { Popover, Upload, Button, message, UploadProps, Image } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { css } from '@emotion/css';
import { useLatest, useUpdateEffect } from 'ahooks';
import { platformUploadImageAcceptMap, platformUploadSizeLimitMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { useCustomUpload } from 'hooks/business/useUploadResource';
import { uploadOssResource } from 'services/api/uploads';
import { forwardRef, Ref, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { useAuthorization } from 'components/Authorization';
import { LoadingOutlined, DeleteOutlined } from '@ant-design/icons';
import produce from 'immer';
import { toBase64 } from 'utils/getImage';
import ImageMessageSelect from './ImageMessageSelect';
import { v4 as uuidV4 } from 'uuid';
import StatusImage from 'views/Conversation/Sider/StatusImage';
import { validatorLazadaImage } from 'utils/validator/lazadaImage';

type CustomUploadProps = {
  btnSize?: number;
  direct?: boolean;
  value?: string[];
  onChange?: (val: string[]) => void;
  maxCount?: number;
  platform?: General.PlatformUpperCase;
  internalStoreId?: string;
  selfHosted?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  conversationId?: string;
};

type ImageFile = {
  uid: string;
  url: string;
  status: 'done' | 'loading';
};

export interface CustomUploadRef {
  handUpload: (file: File) => void;
}

const CustomUpload = forwardRef((props: CustomUploadProps, ref: Ref<CustomUploadRef>) => {
  const {
    btnSize = 56,
    direct = true,
    value = [],
    onChange,
    platform,
    maxCount = 3,
    internalStoreId,
    selfHosted = false,
    disabled = false,
    readonly = false,
    conversationId,
  } = props;
  const [visible, setVisible] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [fileList, setFileList] = useState<ImageFile[]>([]);
  const onChangeRef = useLatest(onChange);
  const { token } = useAuthorization();
  const { t } = useTranslate();
  const customUpload = useCustomUpload();
  const showList = useMemo(() => {
    return [...value, ...fileList];
  }, [fileList, value]);

  useImperativeHandle(ref, () => {
    return {
      handUpload: async (file: File) => {
        const id = uuidV4();

        // size 检测
        if (!platform) {
          if (file.size > 1024 * 1024 * 4) {
            message.warn(
              t('upload.limitWarn', {
                size: `4M`,
              }),
            );
            return;
          }
        } else {
          const { [toLowerCase(platform)]: uploadSizeLimit } = platformUploadSizeLimitMap;
          if (file.size > uploadSizeLimit) {
            message.warn(
              t('upload.limitWarn', {
                size: `${(uploadSizeLimit / 1024 / 1024).toFixed(2)}M`,
              }),
            );
            return;
          }
          const tips = await validatorLazadaImage(file);
          if (tips) {
            message.warn(t(tips));
            return;
          }
        }

        if (selfHosted) {
          syncFileList({
            uid: id,
            status: 'uploading',
            originFileObj: file,
            type: file.type,
          }).then(() => {
            uploadOssResource({
              file,
            })
              .then((res) => {
                const { url } = res;
                syncFileList({
                  uid: id,
                  status: 'done',
                  response: { data: { url } },
                });
              })
              .catch(() => {
                syncFileList({
                  uid: id,
                  status: 'error',
                });
              });
          });
        } else {
          syncFileList({
            uid: id,
            status: 'uploading',
            originFileObj: file,
            type: file.type,
          }).then(() => {
            customUpload(
              {
                onSuccess: () => {
                  syncFileList({
                    uid: id,
                    status: 'done',
                    response: { data: { url: '' } },
                  });
                },
                onError: () => {
                  syncFileList({
                    uid: id,
                    status: 'error',
                  });
                },
                file,
              } as any,
              {
                storeId: internalStoreId,
                fileType: 'IMAGE',
                reqSource: 'TODO',
              },
            );
          });
        }
      },
    };
  });

  useUpdateEffect(() => {
    const done = fileList.find((e) => e.status === 'done');
    if (done) {
      setFileList(
        produce((draft) => {
          const index = draft.findIndex((item) => item.uid === done.uid);
          if (index !== -1) {
            draft.splice(index, 1);
            onChangeRef.current?.([...value, done.url]);
          }
        }),
      );
    }
  }, [fileList, onChangeRef]);

  const customRequest = useCallback(
    (options: Parameters<Required<UploadProps>['customRequest']>[0]) => {
      if (selfHosted) {
        const { file, onProgress, onSuccess, onError } = options;
        uploadOssResource({
          file,
          onProgress,
        })
          .then((res) => {
            const { url } = res;

            onSuccess && onSuccess({ data: { url } }, undefined);
          })
          .catch((err) => {
            onError && onError(err);
          });
      } else {
        customUpload(options, {
          storeId: internalStoreId,
          fileType: 'IMAGE',
          reqSource: 'TODO',
        });
      }
    },
    [customUpload, internalStoreId, selfHosted],
  );

  const handleDelete = useCallback(
    (item: string | { uid: string; url: string }, index: number) => {
      if (typeof item !== 'string' && item.uid) {
        setFileList(
          produce((draft) => {
            const index = draft.findIndex((each) => each.uid === item.uid);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          }),
        );
      } else {
        const newValue = [...value];
        newValue.splice(index, 1);
        onChangeRef.current?.(newValue);
      }
    },
    [onChangeRef, value],
  );

  const syncFileList = useCallback(
    async (file) => {
      const { uid, status, response } = file;
      if (status === 'uploading') {
        const localBlobUrl = await toBase64(new Blob([file.originFileObj], { type: file.type }));
        setFileList(
          produce((draft) => {
            const index = draft.findIndex((item) => item.uid === uid);
            if (index !== -1) {
              draft[index].status = 'loading';
            } else {
              if (draft.length < maxCount - value.length) {
                draft.push({
                  uid,
                  status: 'loading',
                  url: localBlobUrl,
                });
              }
            }
          }),
        );
      }
      if (status === 'done') {
        setFileList(
          produce((draft) => {
            const index = draft.findIndex((item) => item.uid === uid);
            if (index !== -1) {
              draft[index].status = 'done';
              draft[index].url = response.data.url;
            } else {
              if (draft.length < maxCount - value.length) {
                draft.push({
                  uid,
                  status: 'done',
                  url: response.data.url,
                });
              }
            }
          }),
        );
      }
      if (status === 'error' || status === 'removed') {
        setFileList(
          produce((draft) => {
            const index = draft.findIndex((item) => item.uid === uid);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          }),
        );
      }
    },
    [maxCount, value],
  );

  return (
    <div className="flex">
      {visible && conversationId && (
        <ImageMessageSelect
          conversationId={conversationId}
          visible={visible}
          onCancel={() => setVisible(false)}
          maxLength={3 - (fileList.length + value.length)}
          onSelected={(urls) => {
            onChangeRef.current?.([...value, ...urls]);
          }}
        />
      )}
      <div className="flex">
        {showList.map((item, index) => {
          return (
            <div
              key={index}
              style={{ width: `${btnSize}px`, height: `${btnSize}px` }}
              className={css`
                position: relative;
                cursor: pointer;
                margin-right: 8px;
                .mask {
                  display: none;
                }
                &:hover {
                  .mask {
                    display: flex;
                  }
                }
              `}
            >
              {typeof item !== 'string' && item?.status === 'loading' && (
                <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center z-[99]">
                  <LoadingOutlined />
                </div>
              )}
              {!disabled && (
                <div
                  onClick={() => handleDelete(item, index)}
                  className="mask absolute left-0 right-0 bottom-0 z-[101] bg-[#00000066] justify-center items-center py-[2px]"
                >
                  <div>
                    <DeleteOutlined style={{ color: 'white' }} />
                  </div>
                </div>
              )}
              <StatusImage
                style={{ width: `${btnSize}px`, height: `${btnSize}px`, objectFit: 'cover' }}
                preview={{ mask: false }}
                imageRender={(src, props) => {
                  return <Image src={src} {...props} />;
                }}
                src={typeof item === 'string' ? item : item.url}
                alt=""
              />
            </div>
          );
        })}
      </div>

      {fileList.length + value.length < maxCount && !disabled && (
        <Upload
          disabled={readonly}
          showUploadList={false}
          multiple
          headers={{
            [import.meta.env.VITE_AUTH_HEADER as string]: `Bearer ${token}`,
          }}
          accept={
            selfHosted
              ? 'image/*'
              : platformUploadImageAcceptMap[toLowerCase(platform!)].map((_) => `.${_}`).join(',')
          }
          beforeUpload={(file) => {
            if (!platform) {
              if (file.size > 1024 * 1024 * 4) {
                message.warn(
                  t('upload.limitWarn', {
                    size: `4M`,
                  }),
                );

                return false;
              }
              return true;
            } else {
              const { [toLowerCase(platform!)]: uploadSizeLimit } = platformUploadSizeLimitMap;
              if (file.size > uploadSizeLimit) {
                message.warn(
                  t('upload.limitWarn', {
                    size: `${(uploadSizeLimit / 1024 / 1024).toFixed(2)}M`,
                  }),
                );

                return false;
              }
              return (
                platform !== 'LAZADA' ||
                validatorLazadaImage(file).then((tips) => {
                  if (tips) {
                    message.warn(t(tips));
                    return false;
                  }
                  return true;
                })
              );
            }
          }}
          customRequest={customRequest}
          onChange={({ file }) => {
            syncFileList(file);
          }}
        >
          {direct ? (
            <Button
              style={{ width: `${btnSize}px`, height: `${btnSize}px` }}
              type="dashed"
              disabled={readonly}
              icon={<PlusOutlined />}
            ></Button>
          ) : (
            <Popover
              visible={popoverVisible}
              onVisibleChange={setPopoverVisible}
              overlayClassName={css`
                .ant-popover-inner-content {
                  padding: 0 !important;
                }
              `}
              content={
                <div>
                  <div
                    onClick={() => setPopoverVisible(false)}
                    className="leading-[40px] cursor-pointer px-[8px] text-[#515359] border-0 border-b border-solid border-[#f0f2f5]"
                  >
                    {t('ViewsChat.本地上传')}
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setVisible(true);
                      setPopoverVisible(false);
                    }}
                    className="cursor-pointer leading-[40px] px-[8px] text-[#515359]"
                  >
                    {t('scene.chatWorkspace.taskTab.action.uploadFromChatHistory')}
                  </div>
                </div>
              }
              trigger="hover"
            >
              <div onClick={(e) => e.stopPropagation()}>
                <Button
                  style={{ width: `${btnSize}px`, height: `${btnSize}px` }}
                  type="dashed"
                  disabled={readonly}
                  icon={<PlusOutlined />}
                ></Button>
              </div>
            </Popover>
          )}
        </Upload>
      )}
    </div>
  );
});
export default CustomUpload;
